// Salesforce text-length limits, mirrored so the form cannot produce a value
// the record will refuse.
//
// Why this exists. The save posts the whole profile, so ONE over-length value
// makes Salesforce reject the entire record, and the app retried it every ten
// seconds forever. Two advisors hit it on the same morning (Jimmy, 2026-08-28):
//
//   Year Acquired    = "2022 - Inh"  -> STRING_TOO_LONG (max length=4)
//   Year Established = "2000?"       -> STRING_TOO_LONG (max length=4)
//
// Neither was a mistake. One recorded that an asset was inherited, the other
// that they were unsure. The form asked for a year in a free-text box and gave
// no sign that four characters was the ceiling, so both advisors were wedged
// until someone edited the cell by hand.
//
// Note how those got past n() in toSalesforce: parseFloat("2000?") is 2000, so
// the numeric check passes and n() then returns the ORIGINAL string, junk and
// all. Nothing between the input and Salesforce measured the length.
//
// KEYED BY SALESFORCE FIELD, deliberately. The first version of this file was
// keyed by the column's key in our schema, which is wrong: `type` is
// PQ_Asset__c.Type__c (100) in one table and PQ_Insurance__c.Type__c
// (unlimited) in another, and `state` is a 20-char asset field in one place and
// an uncapped Professional Relationship field in another. Keying by our own
// column names silently applied an asset's limit to fields that never had one.
//
// Read from the live PRODUCTION schema on 2026-08-28 (`sf sobject describe`),
// not guessed. If a field is widened in Salesforce, widen or remove it here too
// or the form will keep enforcing a limit that no longer exists.
//
// Fields at Salesforce's default 255 and the Long Text Areas are deliberately
// absent: they are not reachable by ordinary typing, and listing them would
// make this a second, drifting copy of the schema rather than a short list of
// the places that actually bite. External_Key__c is absent too — the app
// generates it, an advisor cannot type into it.
export const SF_TEXT_LIMITS = {
  'Financial_Profile__c.Estate_Plan_Years_Established__c': 4,

  'PQ_Asset__c.Year_Acquired__c': 4,
  'PQ_Asset__c.Year_Established__c': 4,
  'PQ_Asset__c.State__c': 20,
  'PQ_Asset__c.Type__c': 100,
  'PQ_Asset__c.Term__c': 100,
  'PQ_Asset__c.Variable_Fixed__c': 100,

  'PQ_Insurance__c.Owner__c': 100,
  'PQ_Insurance__c.Insured__c': 100,

  'PQ_Income__c.Owner__c': 100,

  'PQ_Employment_Info__c.Job_Title__c': 100,
  'PQ_Employment_Info__c.Employer__c': 100,
  'PQ_Employment_Info__c.Business_Name__c': 255,
  'PQ_Employment_Info__c.Type_of_Business__c': 255,
};

/**
 * The cap for a field or column, or undefined when it is not constrained.
 *
 * The limit lives on the definition itself (`maxLength`, set from
 * SF_TEXT_LIMITS at the point of declaration) because only the definition site
 * knows which Salesforce object the column belongs to. This just reads it, so
 * every input renderer asks the same question the same way.
 */
export function maxLengthFor(def) {
  return def && def.maxLength ? def.maxLength : undefined;
}

/**
 * True when a value already exceeds its cap.
 *
 * An input's own maxLength stops new typing, but it does NOT shorten a value
 * that is already there — one loaded from Salesforce, or entered before this
 * guard existed. Those still need flagging, and still need holding back from
 * the payload, or the record stays wedged exactly as it was.
 */
export function isOverLength(value, max) {
  if (!max) return false;
  if (value === undefined || value === null) return false;
  return String(value).length > max;
}

/**
 * Salesforce fields that hold a WHOLE number (scale 0), so a decimal is
 * refused. Same registry style as the lengths above, and keyed the same way,
 * for the same reason: `retirementAge` and `startingAge` appear on more than
 * one object and only some of them are whole-number.
 *
 * Why this exists. An advisor entered `66.4` as a full retirement age on
 * 2026-08-28. That is not a mistake — Social Security states full retirement
 * age as "66 and 4 months", so the advisor wrote down exactly what the SSA
 * says. `Full_Retirement_Age__c` is scale 0, and Apex refuses a decimal in the
 * JSON DESERIALIZER, before any field-level validation runs. That is why the
 * error names a byte offset and no field at all:
 *
 *   For input string: "66.4" at [line:1, column:4234]
 *
 * `nBounded` did not stop it: it checks the RANGE (0-120, which 66.4 passes)
 * and then returns the original string, decimal intact.
 *
 * Read from the live production schema on 2026-08-28.
 */
export const SF_WHOLE_NUMBER_FIELDS = {
  'Financial_Profile__c.Number_of_Children__c': true,
  'Financial_Profile__c.Number_of_Grandchildren__c': true,
  'PQ_Income__c.Full_Retirement_Age__c': true,
  'PQ_Income__c.Starting_Age__c': true,
  'PQ_Income__c.Pension_Starting_Age__c': true,
  'PQ_Employment_Info__c.Retirement_Age__c': true,
  'PQ_Employment_Info__c.Years_Worked__c': true,
  'PQ_Employment_Info__c.Number_of_Years__c': true,
};

/**
 * True when a value carries a fractional part that its field cannot hold.
 *
 * Deliberately does NOT flag a value that is merely un-parseable — that is a
 * different failure with a different fix. Only a real number with a fraction.
 */
export function isNonInteger(value) {
  if (value === undefined || value === null || value === '') return false;
  const str = String(value).trim();
  if (str === '') return false;
  const num = Number(str);
  if (!Number.isFinite(num)) return false;
  return !Number.isInteger(num);
}
